/**
 * Responsibility: Shape admin preforged-offer data into field-assist metadata and values.
 * Scope: Preforge form metadata/value normalization only; persistence stays elsewhere.
 */
import type { FormMetaData } from 'field-assist'
import {
  COMMERCE_PLAN_IDS,
  type CommercePlanId,
  type PreforgedOffer,
  type PreforgeAiProviderId,
  type PreforgeDensityTargets,
  type PreforgeForgeReport,
  type PreforgeDraft,
  type PreforgeDraftAnalysis,
  type PreforgeRecipe,
  buildPreforgeRecipeDefaults,
  normalizePreforgedOffer,
} from '../../commerce/commerce-contract'

const PLAN_OPTIONS: Array<{ text: string; value: CommercePlanId }> = COMMERCE_PLAN_IDS.map((id) => ({
  text: id[0].toUpperCase() + id.slice(1),
  value: id,
}))

function sourceWorldId(recipe: PreforgeRecipe): string {
  const source = recipe.sourceWorld
  if (source.kind === 'liveRun' || source.kind === 'savedWorld') return source.saveId
  if (source.kind === 'templateWorld') return source.templateId
  return source.presetId
}

function buildRecipeValues(recipe?: PreforgeRecipe): Record<string, string | number> {
  const activeRecipe = recipe ?? buildPreforgeRecipeDefaults({ kind: 'blankPreset', presetId: 'custom' })
  return {
    sourceWorldKind: activeRecipe.sourceWorld.kind,
    sourceWorldId: sourceWorldId(activeRecipe),
    scale: activeRecipe.scale,
    outputMode: activeRecipe.outputMode,
    densityAtlasNodes: activeRecipe.density.atlasNodes,
    densityRooms: activeRecipe.density.rooms,
    densityNpcs: activeRecipe.density.npcs,
    densityCompanions: activeRecipe.density.companions,
    densityItems: activeRecipe.density.items,
    densityQuests: activeRecipe.density.quests,
    densityClocks: activeRecipe.density.clocks,
    densityEvents: activeRecipe.density.events,
    toneMood: activeRecipe.tone.mood,
    toneDangerLevel: activeRecipe.tone.dangerLevel,
    toneSocialFocus: activeRecipe.tone.socialFocus,
    toneExplorationFocus: activeRecipe.tone.explorationFocus,
    toneCombatFocus: activeRecipe.tone.combatFocus,
    physicsGravityConstant: activeRecipe.physics.gravityConstant,
    physicsAtmosphericMix: activeRecipe.physics.atmosphericMix,
    physicsEntropyDecayRate: activeRecipe.physics.entropyDecayRate,
    physicsViscosityThreshold: activeRecipe.physics.viscosityThreshold,
    maxTesterConcurrency: activeRecipe.system.maxTesterConcurrency,
    instancingStrategy: activeRecipe.system.instancingStrategy,
    safetyFilterSensitivity: activeRecipe.system.safetyFilterSensitivity,
    constraintTokenBudget: activeRecipe.constraints.tokenBudget,
    constraintTimeBudgetMs: activeRecipe.constraints.timeBudgetMs,
    constraintForbiddenTopics: activeRecipe.constraints.forbiddenTopics.join('\n'),
    constraintKeepOutRegions: activeRecipe.constraints.keepOutRegions.join('\n'),
    aiProviderId: activeRecipe.aiSelection.providerId,
    aiModel: activeRecipe.aiSelection.model,
  }
}

function buildDraftValues(draft?: PreforgeDraft): Record<string, string> {
  const active = draft ?? {
    brief: '',
    intentSummary: '',
    analysis: {
      intentSummary: '',
      scopeSummary: '',
      keyEntities: [],
      assumptions: [],
      ambiguities: [],
      generationNotes: [],
    } satisfies PreforgeDraftAnalysis,
    seed: { mode: 'create', code: '', title: '', summary: '' },
    vessel: { mode: 'create', code: '', name: '', identity: '', lineage: '', sex: 'unspecified', bio: '' },
    textlets: {
      worldLore: '',
      openingPitch: '',
      npcThreads: '',
      questThreads: '',
      itemHooks: '',
      roomSeeds: '',
    },
  }
  const analysis = active.analysis ?? {
    intentSummary: active.intentSummary || '',
    scopeSummary: '',
    keyEntities: [],
    assumptions: [],
    ambiguities: [],
    generationNotes: [],
  }
  return {
    brief: active.brief || '',
    intentSummary: active.intentSummary || analysis.intentSummary || '',
    analysisScopeSummary: analysis.scopeSummary || '',
    analysisKeyEntities: (analysis.keyEntities ?? []).join('\n'),
    analysisAssumptions: (analysis.assumptions ?? []).join('\n'),
    analysisAmbiguities: (analysis.ambiguities ?? []).join('\n'),
    analysisGenerationNotes: (analysis.generationNotes ?? []).join('\n'),
    title: active.seed.title || active.intentSummary || active.brief || '',
    summary: analysis.scopeSummary || active.textlets.openingPitch || active.seed.summary || '',
    seedMode: active.seed.mode,
    seedCode: active.seed.code,
    seedTitle: active.seed.title,
    seedSummary: active.seed.summary,
    vesselMode: active.vessel.mode,
    vesselCode: active.vessel.code,
    vesselName: active.vessel.name,
    vesselIdentity: active.vessel.identity,
    vesselLineage: active.vessel.lineage,
    vesselSex: active.vessel.sex,
    vesselBio: active.vessel.bio,
    textletWorldLore: active.textlets.worldLore,
    textletOpeningPitch: active.textlets.openingPitch,
    textletNpcThreads: active.textlets.npcThreads,
    textletQuestThreads: active.textlets.questThreads,
    textletItemHooks: active.textlets.itemHooks,
    textletRoomSeeds: active.textlets.roomSeeds,
  }
}

function buildVersioningValues(offer?: Partial<PreforgedOffer>): Record<string, string> {
  const normalized = normalizePreforgedOffer(offer ?? { id: 'draft-review', title: '', worldManifestId: '' })
  return {
    versionLabel: normalized?.versionLabel || '',
    variantOfOfferId: normalized?.variantOfOfferId || '',
    aiProviderId: normalized?.forgeRecipe?.aiSelection.providerId || 'auto',
    aiModel: normalized?.forgeRecipe?.aiSelection.model || '',
  }
}

function buildReviewValues(offer?: Partial<PreforgedOffer>): Record<string, string | number> {
  const normalized = normalizePreforgedOffer(offer ?? { id: 'draft-review', title: '', worldManifestId: '' }) ?? {
    id: 'draft-review',
    kind: 'reality',
    title: '',
    summary: '',
    worldManifestId: '',
    seedCode: '',
    vesselCode: '',
    starterCode: '',
    includedPlanId: 'pro',
    priceModel: 'included',
    priceCents: 0,
    currency: 'USD',
    publishState: 'draft',
    preforgeDraft: {
      brief: '',
      intentSummary: '',
      analysis: {
        intentSummary: '',
        scopeSummary: '',
        keyEntities: [],
        assumptions: [],
        ambiguities: [],
        generationNotes: [],
      },
      seed: { mode: 'create', code: '', title: '', summary: '' },
      vessel: { mode: 'create', code: '', name: '', identity: '', lineage: '', sex: 'unspecified', bio: '' },
      textlets: { worldLore: '', openingPitch: '', npcThreads: '', questThreads: '', itemHooks: '', roomSeeds: '' },
    },
  }
  const draftValues = buildDraftValues(normalized.preforgeDraft)
  const recipeValues = buildRecipeValues(normalized.forgeRecipe)
  const versioningValues = buildVersioningValues(normalized)
  return {
    title: normalized.title,
    summary: normalized.summary,
    ...draftValues,
    ...recipeValues,
    ...versioningValues,
  }
}

function slugify(value: string): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

function buildReportValues(report?: PreforgeForgeReport): Record<string, string> {
  const active = report ?? {
    title: '',
    document: '',
    commercial: '',
    requested: {
      atlasNodes: 0,
      rooms: 0,
      npcs: 0,
      companions: 0,
      items: 0,
      quests: 0,
      clocks: 0,
      events: 0,
    },
    actual: {
      atlasNodes: 0,
      rooms: 0,
      npcs: 0,
      companions: 0,
      items: 0,
      quests: 0,
      clocks: 0,
      events: 0,
    },
    measures: [],
    compliance: [],
    notes: [],
  }
  const measureLines = active.measures.map(measure => `${measure.label}: requested ${measure.requested}, actual ${measure.actual}, delta ${measure.delta >= 0 ? '+' : ''}${measure.delta} (${measure.status})`).join('\n')
  return {
    forgeReportTitle: active.title,
    forgeReportDocument: active.document,
    forgeReportCommercial: active.commercial,
    forgeReportMeasures: measureLines,
    forgeReportCompliance: active.compliance.join('\n'),
    forgeReportNotes: active.notes.join('\n'),
  }
}

export function linkPreforgeFormLabels(host: HTMLElement): void {
  const form = host.querySelector('form.autoform')
  if (!(form instanceof HTMLFormElement)) return
  form.querySelectorAll(':scope > div').forEach((row, index) => {
    const label = row.querySelector('label')
    const control = row.querySelector('input, select, textarea')
    if (!(label instanceof HTMLLabelElement) || !(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) return
    const ref = String(control.getAttribute('ref') || '').trim()
    const labelText = String(label.textContent || '').trim().toLowerCase()
    const id = ref || control.id || labelText.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `preforge-field-${index}`
    control.id = id
    control.setAttribute('data-preforge-field', id)
    label.htmlFor = id
  })
}

export function buildAdminPreforgeFormGroups(): Record<string, { label: string; text: string }> {
  return {
    reality: {
      label: 'Reality Record',
      text: 'Identity, title, summary, and publish state for the stored reality record.',
    },
    versioning: {
      label: 'Reality Versioning',
      text: 'Parent reality linkage and version label for cloned or branched realities.',
    },
    pairing: {
      label: 'Core Artifact Pairing',
      text: 'Assigned seed and vessel. Create or select reusable artifacts before realization.',
    },
    physics: {
      label: 'Physics & Environmental Constants',
      text: 'Physical rules and environmental settings that the forge should respect.',
    },
    narrative: {
      label: 'Narrative & Logical Constraints',
      text: 'Plain-English brief, tone, and textlets that guide the generated world.',
    },
    system: {
      label: 'System Allocation & Safety',
      text: 'Concurrency, instancing, and safety thresholds for the reality.',
    },
    draft: {
      label: 'Brief & Textlets',
      text: 'Plain-English brief plus the editable narrative pieces expanded from it.',
    },
    recipe: {
      label: 'Forge Recipe',
      text: 'Source world, scale, density targets, tone, and constraints.',
    },
    report: {
      label: 'Forge Report',
      text: 'Measured output, compliance notes, and editable commercial copy.',
    },
  }
}

export function buildPreforgeReviewFormGroups(): Record<string, { label: string; text: string }> {
  return {
    record: {
      label: 'Draft Record',
      text: 'The concise identity of the structured draft the process step produced.',
    },
    versioning: {
      label: 'Reality Versioning',
      text: 'Version label, parent reality, and AI selection for this clone or branch.',
    },
    process: {
      label: 'Extraction Review',
      text: 'Plain-English brief, intent summary, extracted entities, ambiguities, and generation guidance.',
    },
    pairing: {
      label: 'Core Artifact Pairing',
      text: 'Assigned seed and vessel. Create or select reusable artifacts before realization.',
    },
    recipe: {
      label: 'Forge Recipe',
      text: 'Source world, scale, density targets, tone, and constraints.',
    },
    physics: {
      label: 'Physics & Environmental Constants',
      text: 'Physical rules and environmental settings that the forge should respect.',
    },
    narrative: {
      label: 'Narrative Textlets',
      text: 'Editable prose fragments the generation engine can consume and you can rewrite.',
    },
    system: {
      label: 'System Allocation & Safety',
      text: 'Concurrency, instancing, and safety thresholds for the reality.',
    },
  }
}

export function buildPreforgeReviewFormMeta(): FormMetaData {
  const meta = buildAdminPreforgeFormMeta() as Record<string, any>
  delete meta.id
  delete meta.kind
  delete meta.worldManifestId
  delete meta.includedPlanId
  delete meta.priceModel
  delete meta.priceCents
  delete meta.currency
  delete meta.publishState
  delete meta.forgeReportTitle
  delete meta.forgeReportDocument
  delete meta.forgeReportCommercial
  delete meta.forgeReportMeasures
  delete meta.forgeReportCompliance
  delete meta.forgeReportNotes
  meta.title = {
    label: 'Draft Title',
    group: 'record',
    componentType: 'input',
    attributes: {
      type: 'text',
      class: 'admin-flag-input',
      placeholder: 'Clockwork Undercity Deluxe',
    },
  }
  meta.summary = {
    label: 'Draft Summary',
    group: 'record',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '3',
      placeholder: 'Concise review copy for the generated reality.',
    },
  }
  meta.versionLabel = {
    label: 'Version Label',
    group: 'versioning',
    componentType: 'input',
    attributes: {
      type: 'text',
      class: 'admin-flag-input',
      placeholder: 'Version 1',
    },
  }
  meta.variantOfOfferId = {
    label: 'Variant Of Reality',
    group: 'versioning',
    componentType: 'input',
    attributes: {
      type: 'text',
      class: 'admin-flag-input',
      placeholder: 'base-reality-id',
    },
  }
  meta.brief = {
    label: 'Source Brief',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '5',
      placeholder: 'Original plain-English description that was processed.',
    },
  }
  meta.intentSummary = {
    label: 'Intent Summary',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '3',
      placeholder: 'A concise statement of what this world is trying to be.',
    },
  }
  meta.analysisScopeSummary = {
    label: 'Scope Summary',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '3',
      placeholder: 'What the draft explicitly covers and what it does not.',
    },
  }
  meta.analysisKeyEntities = {
    label: 'Key Entities',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '4',
      placeholder: 'One entity per line',
    },
  }
  meta.analysisAssumptions = {
    label: 'Assumptions',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '4',
      placeholder: 'One assumption per line',
    },
  }
  meta.analysisAmbiguities = {
    label: 'Ambiguities',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '4',
      placeholder: 'One ambiguity per line',
    },
  }
  meta.analysisGenerationNotes = {
    label: 'Generation Notes',
    group: 'process',
    componentType: 'textarea',
    attributes: {
      class: 'admin-flag-input',
      rows: '4',
      placeholder: 'One note per line',
    },
  }
  meta.aiProviderId = {
    label: 'AI Provider',
    group: 'versioning',
    componentType: 'select',
    options: [
      { text: 'Auto', value: 'auto' },
      { text: 'Vertex Managed', value: 'vertex-managed' },
      { text: 'Gemini', value: 'gemini' },
      { text: 'Anthropic', value: 'anthropic' },
      { text: 'OpenAI', value: 'openai' },
      { text: 'Groq', value: 'groq' },
      { text: 'Ollama', value: 'ollama' },
    ],
    attributes: {
      class: 'admin-flag-select',
    },
  }
  meta.aiModel = {
    label: 'AI Model',
    group: 'versioning',
    componentType: 'input',
    attributes: {
      type: 'text',
      class: 'admin-flag-input',
      placeholder: 'Optional explicit model name',
    },
  }
  meta.seedCode.label = 'Seed Code'
  meta.vesselCode.label = 'Vessel Code'
  return meta as FormMetaData
}

export function buildAdminPreforgeInitialValues(offer?: Partial<PreforgedOffer>): Record<string, string | number | boolean> {
  const normalized: PreforgedOffer = normalizePreforgedOffer(offer ?? { id: 'draft-world', title: '', worldManifestId: '' }) ?? {
    id: 'draft-world',
    kind: 'world',
    title: '',
    summary: '',
    worldManifestId: '',
    seedCode: '',
    vesselCode: '',
    starterCode: '',
    includedPlanId: 'pro',
    priceModel: 'included',
    priceCents: 0,
    currency: 'USD',
    publishState: 'draft',
  }
  const recipeValues = buildRecipeValues(normalized.forgeRecipe)
  const draftValues = buildDraftValues(normalized.preforgeDraft)
  const versioningValues = buildVersioningValues(normalized)
  const reportValues = buildReportValues(normalized.forgeReport)
  return {
    id: normalized.id,
    kind: normalized.kind,
    versionLabel: normalized.versionLabel || '',
    variantOfOfferId: normalized.variantOfOfferId || '',
    title: normalized.title,
    summary: normalized.summary,
    worldManifestId: normalized.worldManifestId,
    starterCode: normalized.starterCode,
    includedPlanId: normalized.includedPlanId,
    priceModel: normalized.priceModel,
    priceCents: normalized.priceCents,
    currency: normalized.currency,
    publishState: normalized.publishState,
    ...draftValues,
    seedCode: normalized.seedCode || draftValues.seedCode || '',
    vesselCode: normalized.vesselCode || draftValues.vesselCode || '',
    ...versioningValues,
    ...recipeValues,
    ...reportValues,
  }
}

export function buildPreforgeReviewInitialValues(offer?: Partial<PreforgedOffer>): Record<string, string | number> {
  return buildReviewValues(offer)
}

export function buildAdminPreforgeFormMeta(): FormMetaData {
  return {
    id: {
      label: 'Offer ID',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'clockwork-undercity-reality',
      },
    },
    kind: {
      label: 'Offer Kind',
      group: 'reality',
      componentType: 'select',
      options: [
        { text: 'World', value: 'world' },
        { text: 'Reality', value: 'reality' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    title: {
      label: 'Public Title',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Clockwork Undercity Deluxe',
      },
    },
    brief: {
      label: 'Plain-English Brief',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '5',
        placeholder: 'Describe the tester-ready reality in plain English. The LLM will turn this into the structured draft.',
      },
    },
    summary: {
      label: 'Summary',
      group: 'reality',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Short merchandising copy for the preforged offer.',
      },
    },
    worldManifestId: {
      label: 'World Manifest ID',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'sample-clockwork-undercity',
      },
    },
    seedCode: {
      label: 'Seed Code',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'SEED-XXXX',
      },
    },
    sourceWorldKind: {
      label: 'Source World Kind',
      group: 'recipe',
      componentType: 'select',
      options: [
        { text: 'Live Run', value: 'liveRun' },
        { text: 'Saved World', value: 'savedWorld' },
        { text: 'Template World', value: 'templateWorld' },
        { text: 'Blank Preset', value: 'blankPreset' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    sourceWorldId: {
      label: 'Source World / Save ID',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'current-save-or-template-id',
      },
    },
    seedMode: {
      label: 'Seed Mode',
      group: 'pairing',
      componentType: 'select',
      options: [
        { text: 'Create New Seed', value: 'create' },
        { text: 'Select Existing Seed', value: 'select' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    seedTitle: {
      label: 'Seed Title',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Clockwork Undercity Seed',
      },
    },
    seedSummary: {
      label: 'Seed Summary',
      group: 'pairing',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Short seed summary for review.',
      },
    },
    scale: {
      label: 'Forge Scale',
      group: 'recipe',
      componentType: 'select',
      options: [
        { text: 'Small', value: 'small' },
        { text: 'Medium', value: 'medium' },
        { text: 'Large', value: 'large' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    outputMode: {
      label: 'Output Mode',
      group: 'recipe',
      componentType: 'select',
      options: [
        { text: 'World Only', value: 'world-only' },
        { text: 'Full Reality', value: 'full-reality' },
        { text: 'Starter Only', value: 'starter-only' },
        { text: 'Published Offer', value: 'published-offer' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    densityAtlasNodes: {
      label: 'Target Atlas Nodes',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityRooms: {
      label: 'Target Rooms',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityNpcs: {
      label: 'Target NPCs',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityCompanions: {
      label: 'Target Companions',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityItems: {
      label: 'Target Items',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityQuests: {
      label: 'Target Quests',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityClocks: {
      label: 'Target Clocks',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    densityEvents: {
      label: 'Target Events',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    toneMood: {
      label: 'Tone Mood',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'balanced and lively',
      },
    },
    toneDangerLevel: {
      label: 'Tone Danger',
      group: 'recipe',
      componentType: 'select',
      options: [
        { text: 'Low', value: 'low' },
        { text: 'Medium', value: 'medium' },
        { text: 'High', value: 'high' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    toneSocialFocus: {
      label: 'Social Focus',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    toneExplorationFocus: {
      label: 'Exploration Focus',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    toneCombatFocus: {
      label: 'Combat Focus',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    physicsGravityConstant: {
      label: 'Gravity Constant',
      group: 'physics',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: '1.00g',
      },
    },
    physicsAtmosphericMix: {
      label: 'Atmospheric Mix',
      group: 'physics',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'mixed breathable',
      },
    },
    physicsEntropyDecayRate: {
      label: 'Entropy Decay Rate',
      group: 'physics',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: '0.05',
      },
    },
    physicsViscosityThreshold: {
      label: 'Viscosity Threshold',
      group: 'physics',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'medium-high',
      },
    },
    maxTesterConcurrency: {
      label: 'Max Tester Concurrency',
      group: 'system',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    instancingStrategy: {
      label: 'Instancing Strategy',
      group: 'system',
      componentType: 'select',
      options: [
        { text: 'Shared Persistent', value: 'shared-persistent' },
        { text: 'Ephemeral Per User', value: 'ephemeral-per-user' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    safetyFilterSensitivity: {
      label: 'Safety Filter Sensitivity',
      group: 'system',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    constraintTokenBudget: {
      label: 'Token Budget',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    constraintTimeBudgetMs: {
      label: 'Time Budget (ms)',
      group: 'recipe',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    constraintForbiddenTopics: {
      label: 'Forbidden Topics',
      group: 'recipe',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'One forbidden topic per line',
      },
    },
    constraintKeepOutRegions: {
      label: 'Keep-Out Regions',
      group: 'recipe',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'One keep-out region per line',
      },
    },
    vesselMode: {
      label: 'Vessel Mode',
      group: 'pairing',
      componentType: 'select',
      options: [
        { text: 'Create New Vessel', value: 'create' },
        { text: 'Select Existing Vessel', value: 'select' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    vesselCode: {
      label: 'Vessel Code (optional)',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'VESSEL-XXXX',
      },
    },
    vesselName: {
      label: 'Vessel Name',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Juki',
      },
    },
    vesselIdentity: {
      label: 'Vessel Identity',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Wandering archivist',
      },
    },
    vesselLineage: {
      label: 'Vessel Lineage',
      group: 'pairing',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Human, construct-born, etc.',
      },
    },
    vesselSex: {
      label: 'Vessel Sex',
      group: 'pairing',
      componentType: 'select',
      options: [
        { text: 'Unspecified', value: 'unspecified' },
        { text: 'Female', value: 'female' },
        { text: 'Male', value: 'male' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    vesselBio: {
      label: 'Vessel Bio',
      group: 'pairing',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '5',
        placeholder: 'Background, persistent conditions, and personality notes.',
      },
    },
    textletWorldLore: {
      label: 'Textlet: World Lore',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'High-level lore, mood, and setting anchors.',
      },
    },
    textletOpeningPitch: {
      label: 'Textlet: Opening Pitch',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Launch pitch and opening hook.',
      },
    },
    textletNpcThreads: {
      label: 'Textlet: NPC Threads',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'NPC archetypes, tensions, and relationships.',
      },
    },
    textletQuestThreads: {
      label: 'Textlet: Quest Threads',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Quest hooks and progress goals.',
      },
    },
    textletItemHooks: {
      label: 'Textlet: Item Hooks',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Notable items, proofs, tools, and artifacts.',
      },
    },
    textletRoomSeeds: {
      label: 'Textlet: Room Seeds',
      group: 'narrative',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '3',
        placeholder: 'Opening rooms and connected expansion seeds.',
      },
    },
    forgeReportTitle: {
      label: 'Forge Report Title',
      group: 'report',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Clockwork Sanctum Forge Report',
      },
    },
    forgeReportDocument: {
      label: 'Forge Report Document',
      group: 'report',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '10',
        placeholder: 'Detailed measured report of what was created and how it matched the requested parameters.',
      },
    },
    forgeReportCommercial: {
      label: 'Forge Commercial Copy',
      group: 'report',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '6',
        placeholder: 'Edited promotional description of the forged world.',
      },
    },
    forgeReportMeasures: {
      label: 'Forge Measures',
      group: 'report',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '8',
        placeholder: 'Atlas nodes: requested 18, actual 24...',
      },
    },
    forgeReportCompliance: {
      label: 'Forge Compliance Notes',
      group: 'report',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '6',
        placeholder: 'One line per compliance note.',
      },
    },
    forgeReportNotes: {
      label: 'Forge Editor Notes',
      group: 'report',
      componentType: 'textarea',
      attributes: {
        class: 'admin-flag-input',
        rows: '4',
        placeholder: 'Anything you want to change before publish.',
      },
    },
    starterCode: {
      label: 'Starter Code (optional)',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'STARTER-XXXX',
      },
    },
    includedPlanId: {
      label: 'Included Plan',
      group: 'reality',
      componentType: 'select',
      options: PLAN_OPTIONS,
      attributes: {
        class: 'admin-flag-select',
      },
    },
    priceModel: {
      label: 'Pricing Model',
      group: 'reality',
      componentType: 'select',
      options: [
        { text: 'Included in Plan', value: 'included' },
        { text: 'Paid Add-on', value: 'paid' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
    priceCents: {
      label: 'Add-on Price (cents)',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    currency: {
      label: 'Currency',
      group: 'reality',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'USD',
      },
    },
    publishState: {
      label: 'Publish State',
      group: 'reality',
      componentType: 'select',
      options: [
        { text: 'Draft', value: 'draft' },
        { text: 'Published', value: 'published' },
        { text: 'Retired', value: 'retired' },
      ],
      attributes: {
        class: 'admin-flag-select',
      },
    },
  }
}

export function buildAdminPreforgeSubmission(values: Record<string, unknown>): PreforgedOffer {
  const seedMode = String(values.seedMode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create'
  const vesselMode = String(values.vesselMode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create'
  const aiProviderId = String(values.aiProviderId ?? 'auto').trim().toLowerCase() as PreforgeAiProviderId
  return normalizePreforgedOffer({
    id: values.id,
    kind: values.kind,
    versionLabel: values.versionLabel,
    variantOfOfferId: values.variantOfOfferId,
    title: values.title,
    summary: values.summary,
    worldManifestId: values.worldManifestId,
    seedCode: values.seedCode,
    vesselCode: values.vesselCode,
    starterCode: values.starterCode,
    includedPlanId: values.includedPlanId,
    priceModel: values.priceModel,
    priceCents: values.priceCents,
    currency: values.currency,
    publishState: values.publishState,
    brief: values.brief,
    seedMode,
    seedTitle: values.seedTitle,
    seedSummary: values.seedSummary,
    vesselMode,
    vesselName: values.vesselName,
    vesselIdentity: values.vesselIdentity,
    vesselLineage: values.vesselLineage,
    vesselSex: values.vesselSex,
    vesselBio: values.vesselBio,
    textletWorldLore: values.textletWorldLore,
    textletOpeningPitch: values.textletOpeningPitch,
    textletNpcThreads: values.textletNpcThreads,
    textletQuestThreads: values.textletQuestThreads,
    textletItemHooks: values.textletItemHooks,
    textletRoomSeeds: values.textletRoomSeeds,
    forgeRecipe: {
      sourceWorld: {
        kind: String(values.sourceWorldKind ?? 'blankPreset'),
        saveId: String(values.sourceWorldId ?? ''),
        templateId: String(values.sourceWorldId ?? ''),
        presetId: String(values.sourceWorldId ?? ''),
      },
      scale: String(values.scale ?? 'medium'),
      density: {
        atlasNodes: Number(values.densityAtlasNodes ?? 0),
        rooms: Number(values.densityRooms ?? 0),
        npcs: Number(values.densityNpcs ?? 0),
        companions: Number(values.densityCompanions ?? 0),
        items: Number(values.densityItems ?? 0),
        quests: Number(values.densityQuests ?? 0),
        clocks: Number(values.densityClocks ?? 0),
        events: Number(values.densityEvents ?? 0),
      } satisfies PreforgeDensityTargets,
      tone: {
        mood: String(values.toneMood ?? ''),
        dangerLevel: String(values.toneDangerLevel ?? 'medium'),
        socialFocus: Number(values.toneSocialFocus ?? 0),
        explorationFocus: Number(values.toneExplorationFocus ?? 0),
        combatFocus: Number(values.toneCombatFocus ?? 0),
      },
      physics: {
        gravityConstant: String(values.physicsGravityConstant ?? ''),
        atmosphericMix: String(values.physicsAtmosphericMix ?? ''),
        entropyDecayRate: String(values.physicsEntropyDecayRate ?? ''),
        viscosityThreshold: String(values.physicsViscosityThreshold ?? ''),
      },
      system: {
        maxTesterConcurrency: Number(values.maxTesterConcurrency ?? 0),
        instancingStrategy: String(values.instancingStrategy ?? 'shared-persistent') as 'shared-persistent' | 'ephemeral-per-user',
        safetyFilterSensitivity: Number(values.safetyFilterSensitivity ?? 0),
      },
      constraints: {
        tokenBudget: Number(values.constraintTokenBudget ?? 0),
        timeBudgetMs: Number(values.constraintTimeBudgetMs ?? 0),
        forbiddenTopics: String(values.constraintForbiddenTopics ?? ''),
        keepOutRegions: String(values.constraintKeepOutRegions ?? ''),
        minimumContentFloors: {},
      },
      outputMode: String(values.outputMode ?? 'full-reality'),
      aiSelection: {
        providerId: ['vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'ollama'].includes(aiProviderId) ? aiProviderId : 'auto',
        model: String(values.aiModel ?? '').trim(),
      },
    },
    preforgeDraft: {
      brief: String(values.brief ?? ''),
      intentSummary: String(values.intentSummary ?? values.brief ?? ''),
      analysis: {
        intentSummary: String(values.intentSummary ?? values.brief ?? ''),
        scopeSummary: String(values.analysisScopeSummary ?? values.summary ?? values.textletOpeningPitch ?? ''),
        keyEntities: String(values.analysisKeyEntities ?? '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
        assumptions: String(values.analysisAssumptions ?? '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
        ambiguities: String(values.analysisAmbiguities ?? '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
        generationNotes: String(values.analysisGenerationNotes ?? '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
      },
      seed: {
        mode: seedMode,
        code: String(values.seedCode ?? ''),
        title: String(values.seedTitle ?? ''),
        summary: String(values.seedSummary ?? ''),
      },
      vessel: {
        mode: vesselMode,
        code: String(values.vesselCode ?? ''),
        name: String(values.vesselName ?? ''),
        identity: String(values.vesselIdentity ?? ''),
        lineage: String(values.vesselLineage ?? ''),
        sex: String(values.vesselSex ?? 'unspecified').toLowerCase() === 'female'
          || String(values.vesselSex ?? '').toLowerCase() === 'male'
          ? String(values.vesselSex ?? '').toLowerCase()
          : 'unspecified',
        bio: String(values.vesselBio ?? ''),
      },
    textlets: {
      worldLore: String(values.textletWorldLore ?? ''),
      openingPitch: String(values.textletOpeningPitch ?? ''),
        npcThreads: String(values.textletNpcThreads ?? ''),
        questThreads: String(values.textletQuestThreads ?? ''),
        itemHooks: String(values.textletItemHooks ?? ''),
        roomSeeds: String(values.textletRoomSeeds ?? ''),
      },
    },
    forgeReport: {
      title: String(values.forgeReportTitle ?? ''),
      document: String(values.forgeReportDocument ?? ''),
      commercial: String(values.forgeReportCommercial ?? ''),
      requested: {
        atlasNodes: Number(values.densityAtlasNodes ?? 0),
        rooms: Number(values.densityRooms ?? 0),
        npcs: Number(values.densityNpcs ?? 0),
        companions: Number(values.densityCompanions ?? 0),
        items: Number(values.densityItems ?? 0),
        quests: Number(values.densityQuests ?? 0),
        clocks: Number(values.densityClocks ?? 0),
        events: Number(values.densityEvents ?? 0),
      } satisfies PreforgeDensityTargets,
      actual: {
        atlasNodes: Number(values.densityAtlasNodes ?? 0),
        rooms: Number(values.densityRooms ?? 0),
        npcs: Number(values.densityNpcs ?? 0),
        companions: Number(values.densityCompanions ?? 0),
        items: Number(values.densityItems ?? 0),
        quests: Number(values.densityQuests ?? 0),
        clocks: Number(values.densityClocks ?? 0),
        events: Number(values.densityEvents ?? 0),
      } satisfies PreforgeDensityTargets,
      measures: String(values.forgeReportMeasures ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          const match = line.match(/^(.+?):\s*requested\s+(\d+),\s*actual\s+(\d+),\s*delta\s+([+-]?\d+)\s+\((below|met|exceeded)\)$/i)
          if (!match) return null
          return {
            label: match[1].trim(),
            requested: Number(match[2]),
            actual: Number(match[3]),
            delta: Number(match[4]),
            status: match[5].toLowerCase() as 'below' | 'met' | 'exceeded',
          }
        })
        .filter(Boolean),
      compliance: String(values.forgeReportCompliance ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean),
      notes: String(values.forgeReportNotes ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean),
    },
  })!
}

export function buildPreforgeReviewSubmission(values: Record<string, unknown>): PreforgedOffer {
  const brief = String(values.brief ?? '').trim()
  const intentSummary = String(values.intentSummary ?? '').trim()
  const title = String(values.title ?? '').trim() || intentSummary || brief || 'Untitled Reality'
  const summary = String(values.summary ?? '').trim() || intentSummary || String(values.analysisScopeSummary ?? '').trim() || brief
  const idBase = slugify(title || brief || intentSummary || 'preforge-review')
  const versionLabel = String(values.versionLabel ?? '').trim()
  const variantOfOfferId = String(values.variantOfOfferId ?? '').trim()
  const seedMode = String(values.seedMode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create'
  const vesselMode = String(values.vesselMode ?? 'create').toLowerCase() === 'select' ? 'select' : 'create'
  const aiProviderId = String(values.aiProviderId ?? 'auto').trim().toLowerCase() as PreforgeAiProviderId
  const keyEntities = String(values.analysisKeyEntities ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  const assumptions = String(values.analysisAssumptions ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  const ambiguities = String(values.analysisAmbiguities ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  const generationNotes = String(values.analysisGenerationNotes ?? '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  const baseOffer = {
    id: idBase || 'preforge-review',
    kind: 'reality',
    title,
    summary,
    worldManifestId: `${idBase || 'preforge-review'}-world`,
    seedCode: String(values.seedCode ?? `${idBase || 'preforge-review'}-seed`),
    vesselCode: String(values.vesselCode ?? `${idBase || 'preforge-review'}-vessel`),
    starterCode: String(values.starterCode ?? `${idBase || 'preforge-review'}-starter`),
    includedPlanId: 'pro',
    priceModel: 'included',
    priceCents: 0,
    currency: 'USD',
    publishState: 'draft',
    versionLabel,
    variantOfOfferId,
    brief,
    seedMode,
    seedTitle: String(values.seedTitle ?? '').trim() || `${title} seed`,
    seedSummary: String(values.seedSummary ?? '').trim() || summary,
    vesselMode,
    vesselName: String(values.vesselName ?? '').trim() || 'Traveler',
    vesselIdentity: String(values.vesselIdentity ?? '').trim() || 'Draft vessel',
    vesselLineage: String(values.vesselLineage ?? '').trim(),
    vesselSex: String(values.vesselSex ?? 'unspecified').toLowerCase() === 'female' || String(values.vesselSex ?? '').toLowerCase() === 'male'
      ? String(values.vesselSex ?? '').toLowerCase()
      : 'unspecified',
    vesselBio: String(values.vesselBio ?? '').trim() || brief,
    textletWorldLore: String(values.textletWorldLore ?? '').trim(),
    textletOpeningPitch: String(values.textletOpeningPitch ?? '').trim(),
    textletNpcThreads: String(values.textletNpcThreads ?? '').trim(),
    textletQuestThreads: String(values.textletQuestThreads ?? '').trim(),
    textletItemHooks: String(values.textletItemHooks ?? '').trim(),
    textletRoomSeeds: String(values.textletRoomSeeds ?? '').trim(),
    sourceWorldKind: String(values.sourceWorldKind ?? 'blankPreset'),
    sourceWorldId: String(values.sourceWorldId ?? ''),
    scale: String(values.scale ?? 'medium'),
    outputMode: String(values.outputMode ?? 'full-reality'),
    densityAtlasNodes: Number(values.densityAtlasNodes ?? 0),
    densityRooms: Number(values.densityRooms ?? 0),
    densityNpcs: Number(values.densityNpcs ?? 0),
    densityCompanions: Number(values.densityCompanions ?? 0),
    densityItems: Number(values.densityItems ?? 0),
    densityQuests: Number(values.densityQuests ?? 0),
    densityClocks: Number(values.densityClocks ?? 0),
    densityEvents: Number(values.densityEvents ?? 0),
    toneMood: String(values.toneMood ?? ''),
    toneDangerLevel: String(values.toneDangerLevel ?? 'medium'),
    toneSocialFocus: Number(values.toneSocialFocus ?? 0),
    toneExplorationFocus: Number(values.toneExplorationFocus ?? 0),
    toneCombatFocus: Number(values.toneCombatFocus ?? 0),
    physicsGravityConstant: String(values.physicsGravityConstant ?? ''),
    physicsAtmosphericMix: String(values.physicsAtmosphericMix ?? ''),
    physicsEntropyDecayRate: String(values.physicsEntropyDecayRate ?? ''),
    physicsViscosityThreshold: String(values.physicsViscosityThreshold ?? ''),
    maxTesterConcurrency: Number(values.maxTesterConcurrency ?? 0),
    instancingStrategy: String(values.instancingStrategy ?? 'shared-persistent'),
    safetyFilterSensitivity: Number(values.safetyFilterSensitivity ?? 0),
    constraintTokenBudget: Number(values.constraintTokenBudget ?? 0),
    constraintTimeBudgetMs: Number(values.constraintTimeBudgetMs ?? 0),
    constraintForbiddenTopics: String(values.constraintForbiddenTopics ?? ''),
    constraintKeepOutRegions: String(values.constraintKeepOutRegions ?? ''),
    analysisIntentSummary: intentSummary,
    analysisScopeSummary: String(values.analysisScopeSummary ?? '').trim() || summary,
    analysisKeyEntities: keyEntities.join('\n'),
    analysisAssumptions: assumptions.join('\n'),
    analysisAmbiguities: ambiguities.join('\n'),
    analysisGenerationNotes: generationNotes.join('\n'),
  }
  return normalizePreforgedOffer({
    ...baseOffer,
    forgeRecipe: {
      sourceWorld: {
        kind: String(values.sourceWorldKind ?? 'blankPreset'),
        saveId: String(values.sourceWorldId ?? ''),
        templateId: String(values.sourceWorldId ?? ''),
        presetId: String(values.sourceWorldId ?? ''),
      },
      scale: String(values.scale ?? 'medium'),
      density: {
        atlasNodes: Number(values.densityAtlasNodes ?? 0),
        rooms: Number(values.densityRooms ?? 0),
        npcs: Number(values.densityNpcs ?? 0),
        companions: Number(values.densityCompanions ?? 0),
        items: Number(values.densityItems ?? 0),
        quests: Number(values.densityQuests ?? 0),
        clocks: Number(values.densityClocks ?? 0),
        events: Number(values.densityEvents ?? 0),
      } satisfies PreforgeDensityTargets,
      tone: {
        mood: String(values.toneMood ?? ''),
        dangerLevel: String(values.toneDangerLevel ?? 'medium'),
        socialFocus: Number(values.toneSocialFocus ?? 0),
        explorationFocus: Number(values.toneExplorationFocus ?? 0),
        combatFocus: Number(values.toneCombatFocus ?? 0),
      },
      physics: {
        gravityConstant: String(values.physicsGravityConstant ?? ''),
        atmosphericMix: String(values.physicsAtmosphericMix ?? ''),
        entropyDecayRate: String(values.physicsEntropyDecayRate ?? ''),
        viscosityThreshold: String(values.physicsViscosityThreshold ?? ''),
      },
      system: {
        maxTesterConcurrency: Number(values.maxTesterConcurrency ?? 0),
        instancingStrategy: String(values.instancingStrategy ?? 'shared-persistent') as 'shared-persistent' | 'ephemeral-per-user',
        safetyFilterSensitivity: Number(values.safetyFilterSensitivity ?? 0),
      },
      constraints: {
        tokenBudget: Number(values.constraintTokenBudget ?? 0),
        timeBudgetMs: Number(values.constraintTimeBudgetMs ?? 0),
        forbiddenTopics: String(values.constraintForbiddenTopics ?? ''),
        keepOutRegions: String(values.constraintKeepOutRegions ?? ''),
        minimumContentFloors: {},
      },
      outputMode: String(values.outputMode ?? 'full-reality'),
      aiSelection: {
        providerId: ['vertex-managed', 'gemini', 'anthropic', 'openai', 'groq', 'ollama'].includes(aiProviderId) ? aiProviderId : 'auto',
        model: String(values.aiModel ?? '').trim(),
      },
    },
    preforgeDraft: {
      brief,
      intentSummary,
      analysis: {
        intentSummary,
        scopeSummary: String(values.analysisScopeSummary ?? '').trim() || summary,
        keyEntities,
        assumptions,
        ambiguities,
        generationNotes,
      },
      seed: {
        mode: seedMode,
        code: String(values.seedCode ?? `${idBase || 'preforge-review'}-seed`),
        title: String(values.seedTitle ?? '').trim() || `${title} seed`,
        summary: String(values.seedSummary ?? '').trim() || summary,
      },
      vessel: {
        mode: vesselMode,
        code: String(values.vesselCode ?? `${idBase || 'preforge-review'}-vessel`),
        name: String(values.vesselName ?? '').trim() || 'Traveler',
        identity: String(values.vesselIdentity ?? '').trim() || 'Draft vessel',
        lineage: String(values.vesselLineage ?? '').trim(),
        sex: String(values.vesselSex ?? 'unspecified').toLowerCase() === 'female'
          || String(values.vesselSex ?? '').toLowerCase() === 'male'
          ? String(values.vesselSex ?? '').toLowerCase()
          : 'unspecified',
        bio: String(values.vesselBio ?? '').trim() || brief,
      },
      textlets: {
        worldLore: String(values.textletWorldLore ?? '').trim(),
        openingPitch: String(values.textletOpeningPitch ?? '').trim(),
        npcThreads: String(values.textletNpcThreads ?? '').trim(),
        questThreads: String(values.textletQuestThreads ?? '').trim(),
        itemHooks: String(values.textletItemHooks ?? '').trim(),
        roomSeeds: String(values.textletRoomSeeds ?? '').trim(),
      },
    },
  })!
}

export function formatPreforgeRecipeSummary(recipe?: PreforgeRecipe): string {
  if (!recipe) return 'Legacy capture without recipe'
  const source = recipe.sourceWorld.kind === 'liveRun' || recipe.sourceWorld.kind === 'savedWorld'
    ? `${recipe.sourceWorld.kind}:${recipe.sourceWorld.saveId}`
    : recipe.sourceWorld.kind === 'templateWorld'
      ? `template:${recipe.sourceWorld.templateId}`
      : `preset:${recipe.sourceWorld.presetId}`
  const ai = recipe.aiSelection.providerId === 'auto'
    ? 'auto-ai'
    : recipe.aiSelection.model
      ? `${recipe.aiSelection.providerId}:${recipe.aiSelection.model}`
      : recipe.aiSelection.providerId
  return `${source} · ${recipe.scale} · ${recipe.density.rooms} rooms · ${recipe.density.npcs} NPCs · ${recipe.outputMode} · ${ai}`
}

export function formatPreforgeDraftSummary(draft?: PreforgedOffer['preforgeDraft']): string {
  if (!draft) return 'No draft expansion yet'
  const seed = draft.seed.code ? `${draft.seed.mode}:${draft.seed.code}` : draft.seed.mode
  const vessel = draft.vessel.code ? `${draft.vessel.mode}:${draft.vessel.code}` : draft.vessel.mode
  const brief = draft.intentSummary || draft.analysis?.scopeSummary || draft.brief || draft.seed.summary || draft.vessel.bio || 'Draft ready'
  return `${seed} · ${vessel} · ${brief.slice(0, 90)}`
}

export function formatPreforgeReportSummary(report?: PreforgedOffer['forgeReport']): string {
  if (!report) return 'No forge report yet'
  const measure = report.measures[0]
  const headline = report.title || 'Forge report'
  return `${headline} · ${measure ? `${measure.label} ${measure.actual}/${measure.requested}` : 'Measured' }`
}

export function describePreforgePairing(offer?: Partial<PreforgedOffer>): string {
  if (!offer) return 'No reality loaded'
  if (offer.kind === 'world') {
    return offer.vesselCode ? 'World-only shell with vessel reference' : 'World-only shell'
  }
  const hasVessel = Boolean(String(offer.vesselCode ?? '').trim())
  const hasStarter = Boolean(String(offer.starterCode ?? '').trim())
  const version = String(offer.versionLabel ?? '').trim()
  const versionText = version ? ` · ${version}` : ''
  if (hasVessel && hasStarter) return `Reality pair ready${versionText}`
  if (hasVessel || hasStarter) return `Reality partially paired${versionText}`
  return `Reality missing pair data${versionText}`
}

export function setPreforgeFormLocked(host: HTMLElement, locked: boolean): void {
  const form = host.querySelector('form.autoform')
  if (!(form instanceof HTMLFormElement)) return
  form.dataset.state = locked ? 'locked' : 'editable'
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea').forEach((field) => {
    if (locked) field.setAttribute('disabled', 'true')
    else field.removeAttribute('disabled')
  })
}
